// platform-mfa-totp: אפליקציית אימות (TOTP) — GoTrue מנהל את הגורמים (/auth/v1/factors: הצטרפות עם QR ומפתח, אתגר, אימות →
// סשן חדש ב-aal2, הסרה); ה-api רק אומר מה הסשן צריך (GET /api/auth/mfa/status, מותר גם ב-aal1) ואוכף: עד הקוד כל נתיב אחר
// עונה 401 mfa_required. "אחר כך" בימי החסד נזכר ללשונית (sessionStorage) — לא לנצח.
import { useSyncExternalStore } from 'react';
import { MfaStatus } from '@aibify/core';
import { json } from './api.ts';
import { adoptTokens, getAccessToken, getSession } from './auth.ts';

export type { MfaStatus };
export type Enrollment = { factorId: string; qrSvg: string; secret: string; uri: string };
export type VerifyResult = 'ok' | 'wrong' | 'rate' | 'failed';

const LATER_KEY = 'aibify.mfa.later';

export const fetchMfaStatus = () => json(MfaStatus, '/api/auth/mfa/status');

async function gotrue(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  return fetch(`/auth/v1${path}`, { ...init, headers: { 'content-type': 'application/json', ...(init.headers ?? {}), authorization: `Bearer ${token}` } });
}

/** ה-QR של GoTrue הוא SVG כטקסט (או data URI) — מקור ל-<img>. */
export function qrSrc(qr: string): string {
  return qr.startsWith('data:') ? qr : `data:image/svg+xml;utf8,${encodeURIComponent(qr)}`;
}

/** הצטרפות: גורם TOTP חדש (לא מאומת עד הקוד). 'disabled' = GoTrue בלי MFA; 'aal2' = יש כבר גורם וצריך קוד קודם (החלפת מכשיר). */
export async function enrollTotp(issuer: string): Promise<Enrollment | 'disabled' | 'aal2' | 'failed'> {
  try {
    const r = await gotrue('/factors', { method: 'POST', body: JSON.stringify({ factor_type: 'totp', friendly_name: `aibify ${new Date().toISOString().slice(0, 16)}`, issuer }) });
    const b = (await r.json().catch(() => ({}))) as { id?: string; totp?: { qr_code?: string; secret?: string; uri?: string }; msg?: string; error_code?: string };
    if (r.status === 403 || /aal2/i.test(b.msg ?? '')) return 'aal2';
    if (!r.ok) return /disabled|not enabled/i.test(b.msg ?? '') ? 'disabled' : 'failed';
    if (!b.id || !b.totp?.qr_code || !b.totp.secret) return 'failed';
    return { factorId: b.id, qrSvg: b.totp.qr_code, secret: b.totp.secret, uri: b.totp.uri ?? '' };
  } catch {
    return 'failed';
  }
}

/** אתגר + אימות קוד → GoTrue מחזיר סשן חדש (aal2) שמחליף את הנוכחי. */
export async function verifyTotp(factorId: string, code: string): Promise<VerifyResult> {
  try {
    const ch = await gotrue(`/factors/${encodeURIComponent(factorId)}/challenge`, { method: 'POST', body: '{}' });
    if (ch.status === 429) return 'rate';
    const c = (await ch.json().catch(() => ({}))) as { id?: string };
    if (!ch.ok || !c.id) return 'failed';
    const v = await gotrue(`/factors/${encodeURIComponent(factorId)}/verify`, { method: 'POST', body: JSON.stringify({ challenge_id: c.id, code }) });
    if (v.status === 429) return 'rate';
    const b = (await v.json().catch(() => ({}))) as { access_token?: string; refresh_token?: string };
    if (!v.ok) return v.status >= 500 ? 'failed' : 'wrong';
    if (!b.access_token || !b.refresh_token) return 'failed';
    adoptTokens(b.access_token, b.refresh_token);
    return 'ok';
  } catch {
    return 'failed';
  }
}

/** הגורמים של המשתמש אצל GoTrue (להחלפת מכשיר: להסיר את הישנים אחרי שהחדש אומת). */
export async function listFactors(): Promise<Array<{ id: string; status: string; type: string }>> {
  const r = await gotrue('/user');
  const b = (await r.json().catch(() => ({}))) as { factors?: Array<{ id: string; status: string; factor_type: string }> };
  return (b.factors ?? []).map((f) => ({ id: f.id, status: f.status, type: f.factor_type }));
}

export async function unenroll(factorId: string): Promise<boolean> {
  try {
    return (await gotrue(`/factors/${encodeURIComponent(factorId)}`, { method: 'DELETE' })).ok;
  } catch {
    return false;
  }
}

// ---------- מצב משותף (השער, הבאנר, ההגדרות) ----------
let status: MfaStatus | null = null;
let statusFor: string | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const getMfaStatus = () => status;
export function setMfaStatus(s: MfaStatus | null, token: string | null = getSession()?.accessToken ?? null) {
  status = s;
  statusFor = token;
  notify();
}
/** המצב השמור תקף רק ל-access token שעבורו נטען (אימות קוד / רענון מחליפים אותו). */
export const mfaStatusFor = (token: string) => (statusFor === token ? status : null);
export function useMfaStatus(): MfaStatus | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => void listeners.delete(l);
    },
    () => status,
    () => status,
  );
}

/** "אחר כך" בימי החסד — ללשונית הזו, למשתמש הזה. */
export function laterChosen(userId: string): boolean {
  try {
    return sessionStorage.getItem(LATER_KEY) === userId;
  } catch {
    return false;
  }
}
export function chooseLater(userId: string) {
  try {
    sessionStorage.setItem(LATER_KEY, userId);
  } catch {}
}

/** קלט קוד: ספרות בלבד, עד 6 (הדבקה של "123 456" / "123-456" עובדת). */
export const normalizeCode = (raw: string) => raw.replace(/\D/g, '').slice(0, 6);
export const codeComplete = (code: string) => /^\d{6}$/.test(code);

/** תאריך סוף החסד בעברית ("12.11"). */
export const graceDateHe = (iso: string) => new Date(iso).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', timeZone: 'Asia/Jerusalem' });
